// src/pages/FeedbackPage.js
import React, { useState, useContext, useEffect } from 'react';
import styles from './FeedbackPage.module.css';
import AuthContext from '../../context/AuthContext';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import { useNavigate } from 'react-router-dom';

import posthog from 'posthog-js';

// Components
import QuestionSection1 from './components/QuestionOne';
import QuestionSection2 from './components/QuestionTwo';
import QuestionSection3 from './components/QuestionThree';
import QuestionSection4 from './components/QuestionFour';
import QuestionSection5 from './components/QuestionFive';
import QuestionSection6 from './components/QuestionSix';
import ThankYouCard from './components/ThankyouCard';
import ProgressBar from './UI/ProgressBar';

// Import the API function
import { submitFeedback } from '../../services/feedbackApi';

const FeedbackPage = () => {
    const authCtx = useContext(AuthContext);

    // --- State for feedback form fields ---
    const [q1Rating, setQ1Rating] = useState(0); // Description Rating
    const [q2Rating, setQ2Rating] = useState(0); // Title/Bullets Rating
    const [q3Rating, setQ3Rating] = useState(0); // Infographics Rating
    const [q4Improvement, setQ4Improvement] = useState(''); // Improvement Text
    const [q5Referral, setQ5Referral] = useState(''); // Referral Yes/No
    const [generalFeedback, setGeneralFeedback] = useState(''); // Optional general comments

    // State for submission status
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [activeSection, setActiveSection] = useState(1);

    const getSectionName = (sectionNumber) => {
        switch(sectionNumber) {
            case 1: return 'description_rating';
            case 2: return 'title_bullets_rating';
            case 3: return 'infographics_rating';
            case 4: return 'improvement_suggestion';
            case 5: return 'referral_question';
            case 6: return 'general_feedback';
            default: return `section_${sectionNumber}`;
        }
    };

        // Identify user in PostHog when the component mounts
        useEffect(() => {
            if (authCtx.user && (authCtx.user.username || authCtx.user.sub)) {
                posthog.identify(
                    authCtx.user.username || authCtx.user.sub,
                    {
                        email: authCtx.user.email || '',
                        name: authCtx.user.name || '',
                        user_type: authCtx.user.user_type || ''
                    }
                );
                
                // Track the feedback page view
                posthog.capture('feedback_page_viewed');
            }
        }, [authCtx.user]);
    
        // Track section changes
        useEffect(() => {
            if (activeSection > 1) {
                posthog.capture('feedback_section_changed', {
                    section_number: activeSection,
                    section_name: getSectionName(activeSection)
                });
            }
        }, [activeSection]);

    // Progress calculation
    const calculateProgress = () => {
        let completed = 0;
        if (q1Rating > 0) completed++;
        if (q2Rating > 0) completed++;
        if (q3Rating > 0) completed++;
        if (q4Improvement.trim().length > 0) completed++;
        if (q5Referral) completed++;
        if (generalFeedback.trim().length > 0) completed++;
        
        // We're counting q4 and generalFeedback even though they're optional
        return (completed / 6) * 100;
    };

    // --- Handlers ---
    const handleRatingChange = (questionNumber, rating) => {
        setMessage(''); setIsError(false); // Clear message on interaction
        if (questionNumber === 1) {
            setQ1Rating(rating);
            // Track rating for question 1
            posthog.capture('feedback_rating_provided', {
                question_number: 1,
                question_type: 'description_rating',
                rating: rating
            });
        }
        else if (questionNumber === 2) {
            setQ2Rating(rating);
            // Track rating for question 2
            posthog.capture('feedback_rating_provided', {
                question_number: 2,
                question_type: 'title_bullets_rating',
                rating: rating
            });
        }
        else if (questionNumber === 3) {
            setQ3Rating(rating);
            // Track rating for question 3
            posthog.capture('feedback_rating_provided', {
                question_number: 3,
                question_type: 'infographics_rating',
                rating: rating
            });
        }
    };

    const handleImprovementChange = (event) => {
        setQ4Improvement(event.target.value);
        setMessage(''); setIsError(false);
    };

    const handleReferralChange = (event) => {
        setQ5Referral(event.target.value);
        setMessage(''); setIsError(false);
        
        // Track referral response
        posthog.capture('feedback_referral_response', {
            would_refer: event.target.value
        });

    };

    const handleGeneralFeedbackChange = (event) => {
        setGeneralFeedback(event.target.value);
        setMessage(''); setIsError(false);
    };

    const moveToNextSection = () => {
        if (activeSection < 6) {
            setActiveSection(activeSection + 1);
            // Smooth scroll to the section
            setTimeout(() => {
                const section = document.getElementById(`section-${activeSection + 1}`);
                if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        }
    };

    const moveToPreviousSection = () => {
        if (activeSection > 1) {
            setActiveSection(activeSection - 1);
            // Smooth scroll to the section
            setTimeout(() => {
                const section = document.getElementById(`section-${activeSection - 1}`);
                if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        }
    };

    // Auto-advance to next section when completing a section
    useEffect(() => {
        if (q1Rating > 0 && activeSection === 1) {
            moveToNextSection();
        }
    }, [q1Rating]);

    useEffect(() => {
        if (q2Rating > 0 && activeSection === 2) {
            moveToNextSection();
        }
    }, [q2Rating]);

    useEffect(() => {
        if (q3Rating > 0 && activeSection === 3) {
            moveToNextSection();
        }
    }, [q3Rating]);

    useEffect(() => {
        if (q5Referral && activeSection === 5) {
            moveToNextSection();
        }
    }, [q5Referral]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setMessage('');
        setIsError(false);

        // Basic Validation (ensure ratings are given and referral choice made)
        if (q1Rating === 0 || q2Rating === 0 || q3Rating === 0 || !q5Referral) {
            setMessage('Please answer all required questions (Ratings and Referral question).');
            setIsError(true);
            setIsLoading(false);

            // Track incomplete submission
            posthog.capture('feedback_submission_incomplete', {
                missing_fields: [
                    q1Rating === 0 ? 'q1_rating' : null,
                    q2Rating === 0 ? 'q2_rating' : null,
                    q3Rating === 0 ? 'q3_rating' : null,
                    !q5Referral ? 'q5_referral' : null
                ].filter(Boolean)
            });

            return;
        }

        if (!authCtx.token) {
            setMessage('Authentication required to submit feedback.');
            setIsError(true);
            setIsLoading(false);
            // Track auth error
            posthog.capture('feedback_submission_auth_error');
            return;
        }

        // --- Prepare Payload ---
        const feedbackData = {
            rating_description: q1Rating,
            rating_title_bullets: q2Rating,
            rating_infographics: q3Rating,
            improvement_suggestion: q4Improvement.trim(),
            would_refer: q5Referral,
            general_comment: generalFeedback.trim(),
            submitted_at: new Date().toISOString(), // Add timestamp
        };

        // --- Submit feedback using the API service ---
        try {
            await submitFeedback(authCtx.token, feedbackData);

            // --- On Success ---
            setMessage('Thank you for your valuable feedback!');
            setIsError(false);
            setIsSubmitted(true);

            // Calculate overall rating average
            const avgRating = ((q1Rating + q2Rating + q3Rating) / 3).toFixed(1);
            
            // Track successful feedback submission in PostHog
            posthog.capture('feedback_submitted', {
                distinct_id: authCtx.user?.username || authCtx.user?.sub,
                feedback_type: 'product_feedback',
                rating_description: q1Rating,
                rating_title_bullets: q2Rating, 
                rating_infographics: q3Rating,
                average_rating: parseFloat(avgRating),
                has_improvement_suggestion: q4Improvement.trim().length > 0,
                would_refer: q5Referral,
                has_general_comment: generalFeedback.trim().length > 0,
                submission_timestamp: new Date().toISOString()
            });
            
            // Update user properties based on feedback
            posthog.people.set({
                last_feedback_date: new Date().toISOString(),
                last_feedback_rating: parseFloat(avgRating),
                would_refer: q5Referral === 'yes'
            });
            
            // Clear form
            setQ1Rating(0);
            setQ2Rating(0);
            setQ3Rating(0);
            setQ4Improvement('');
            setQ5Referral('');
            setGeneralFeedback('');

        } catch (error) {
            // --- On Error ---
            setMessage(error.message || 'Failed to submit feedback. Please try again.');
            setIsError(true);

            // Track submission error
            posthog.capture('feedback_submission_error', {
                error_message: error.message || 'API error'
            });

        } finally {
            setIsLoading(false);
        }
    };

    // Progress percentage
    const progressPercentage = calculateProgress();
    const navigate = useNavigate();
    const [targetPath, setTargetPath] = useState(null);
    
    const handleGeneratorClick = (path) => {
    
        // Track click on post-feedback navigation
        posthog.capture('post_feedback_navigation', {
            destination: path
        });

        setTargetPath(path);
        navigate(path);
      };

    return (
        <div className={styles.feedbackContainer}>
            {isLoading && <LoadingOverlay message="Submitting Feedback..." />}
            {isSubmitted ? (
                 <ThankYouCard handleGeneratorClick={handleGeneratorClick} />
            ) : (
                <>
                    <div className={styles.header}>
                        <h1>Share Your Feedback</h1>
                    </div>
                    <ProgressBar progressPercentage={progressPercentage} />
                    <div className={styles.formCard}>
                        <form onSubmit={handleSubmit} className={styles.feedbackForm}>
                            {/* --- Questions --- */}
                            <QuestionSection1 {...{ q1Rating, handleRatingChange, isLoading, moveToNextSection, activeSection }} />
                            <QuestionSection2 {...{ q2Rating, handleRatingChange, isLoading, moveToNextSection, moveToPreviousSection, activeSection }} />
                            <QuestionSection3 {...{ q3Rating, handleRatingChange, isLoading, moveToNextSection, moveToPreviousSection, activeSection }} />
                            <QuestionSection4 {...{ q4Improvement, handleImprovementChange, isLoading, moveToNextSection, moveToPreviousSection, activeSection }} />
                            <QuestionSection5 {...{ q5Referral, handleReferralChange, isLoading, moveToNextSection, moveToPreviousSection, activeSection }} />
                            <QuestionSection6 activeSection={activeSection} generalFeedback={generalFeedback} handleGeneralFeedbackChange={handleGeneralFeedbackChange} moveToPreviousSection={moveToPreviousSection} isLoading={isLoading} isError={isError} message={message} q1Rating={q1Rating} q2Rating={q2Rating} q3Rating={q3Rating} q5Referral={q5Referral} />
                        </form>
                    </div>
                </>
            )}
        </div>
    );
};

export default FeedbackPage;